import type { ReactNode } from "react";
import { Link } from "react-router";
import { ChevronLeft, ShoppingCart, type LucideIcon } from "lucide-react";
import logoMark from "../../images/logo-mark.png";
import { Icon, SocialButton } from "../ui";
import type { SocialProvider } from "../../api/account";

/**
 * The sign-in screens own the viewport (design system CustomerLogin): a brand field on the
 * left, the form on the right, and no storefront header. Under 840px the brand field becomes
 * a slim logo band so the form clears the fold.
 */
export function AuthLayout({ children, back = "/" }: { children: ReactNode; back?: string }) {
  return (
    <main className="grid min-h-dvh grid-rows-[auto_1fr] bg-surface-card min-[840px]:grid-cols-2 min-[840px]:grid-rows-none">
      <div className="relative isolate flex h-[76px] items-center overflow-hidden bg-green-900 px-4 min-[840px]:h-auto min-[840px]:flex-col min-[840px]:items-start min-[840px]:justify-between min-[840px]:p-10">
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-(image:--gradient-sprout) opacity-[.28]" />
        <img aria-hidden="true" src={logoMark} alt="" className="absolute right-[-14%] bottom-[-16%] -z-10 hidden w-[78%] opacity-[.09] min-[840px]:block" />
        <Link to="/" className="flex items-center gap-4 border-b-0" aria-label="Kalikaya parts catalogue, home">
          <img src={logoMark} alt="" width={47} height={44} className="h-10 w-auto min-[840px]:h-11" />
          <span className="flex flex-col gap-[3px]">
            <span className="font-display text-[21px] leading-none font-extrabold tracking-[-0.01em] text-white">KALIKAYA</span>
            <span className="type-eyebrow text-[8px] tracking-[0.14em] text-green-300">ONLINE PARTS CATALOGUE</span>
          </span>
        </Link>
        <div className="hidden max-w-[420px] min-[840px]:block">
          <h2 className="mb-5 type-display [font-size:var(--text-3xl)] tracking-tight text-white">
            The right part,
            <br />
            first time.
          </h2>
          <p className="mb-0 type-body [font-size:var(--text-md)] text-green-100">
            Find your machine, open its diagram, and order the part by the number printed on it.
          </p>
        </div>
      </div>

      <div className="flex flex-col px-4 py-8 sm:px-[clamp(var(--space-7),6vw,var(--space-12))] min-[840px]:justify-center">
        <div className="mx-auto w-full max-w-[400px]">
          <div className="mb-9 flex justify-end">
            <Link to={back} className="inline-flex min-h-11 items-center gap-2 border-b-0 type-small font-semibold">
              <Icon icon={ChevronLeft} size={15} />
              Continue shopping
            </Link>
          </div>
          {children}
        </div>
      </div>
    </main>
  );
}

export function AuthHeading({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <>
      <h1 className="mb-3 type-h1 [font-size:var(--text-2xl)] tracking-tight">{title}</h1>
      {children && <p className="mb-7 type-body text-text-muted">{children}</p>}
    </>
  );
}

/** The round icon above a "check your email" or "done" message. */
export function AuthBadge({ icon, tone = "success" }: { icon: LucideIcon; tone?: "success" | "danger" }) {
  return (
    <span
      className={
        tone === "success"
          ? "mb-6 inline-flex size-12 items-center justify-center rounded-full bg-success-100 text-success-700"
          : "mb-6 inline-flex size-12 items-center justify-center rounded-full bg-danger-100 text-danger-700"
      }
    >
      <Icon icon={icon} size={24} />
    </span>
  );
}

/** A button styled as a link, for secondary actions inside a form. */
export function TextButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="min-h-11 cursor-pointer border-none bg-transparent p-0 type-small font-semibold text-text-link hover:underline">
      {children}
    </button>
  );
}

/** An error that belongs to the whole form, not one field. */
export function FormError({ children }: { children?: string }) {
  if (!children) return null;
  return (
    <p role="alert" className="mt-5 mb-0 rounded-md bg-danger-100 px-4 py-3 type-small text-danger-700">
      {children}
    </p>
  );
}

/** A message about the form as a whole, with an optional next step (design system AuthFlow). */
export function Notice({ tone = "info", icon, children, action }: { tone?: "info" | "danger"; icon: LucideIcon; children: ReactNode; action?: ReactNode }) {
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={
        tone === "danger"
          ? "mt-5 flex items-start gap-4 rounded-md bg-danger-100 px-5 py-4 type-small text-danger-700"
          : "mt-5 flex items-start gap-4 rounded-md bg-info-100 px-5 py-4 type-small text-info-700"
      }
    >
      <Icon icon={icon} size={18} className="mt-px flex-none" />
      <span className="flex min-w-0 flex-1 flex-col items-start gap-3">
        <span className="[text-wrap:pretty]">{children}</span>
        {action}
      </span>
    </div>
  );
}

/** A phone number or email repeated back, in the mono face. */
export function Repeated({ children }: { children: ReactNode }) {
  return <strong className="whitespace-nowrap type-sku [font-size:var(--text-base)] text-text-strong">{children}</strong>;
}

export function OrDivider() {
  return (
    <div role="separator" aria-label="or" className="mt-7 mb-6 flex items-center gap-4 type-small text-text-muted">
      <span className="h-px flex-1 bg-border-subtle" />
      or
      <span className="h-px flex-1 bg-border-subtle" />
    </div>
  );
}

/** So a customer sent here from checkout knows the basket is waiting. */
export function BasketNote({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <div className="mb-6 flex items-center gap-3 rounded-md bg-surface-tint-green px-5 py-4 type-small text-green-800">
      <Icon icon={ShoppingCart} size={16} />
      <span>
        {count} item{count === 1 ? "" : "s"} waiting in your basket
      </span>
    </div>
  );
}

/** Google first, then Facebook, under an "or". Only the providers the server has set up. */
export function SocialButtons({
  available,
  busy,
  onChoose,
}: {
  available: Record<SocialProvider, boolean>;
  busy: SocialProvider | null;
  onChoose: (provider: SocialProvider) => void;
}) {
  if (!available.google && !available.facebook) return null;
  return (
    <>
      <OrDivider />
      <div className="flex flex-col gap-3">
        {available.google && (
          <SocialButton provider="google" disabled={busy !== null} onClick={() => onChoose("google")}>
            {busy === "google" ? "Opening Google…" : undefined}
          </SocialButton>
        )}
        {available.facebook && (
          <SocialButton provider="facebook" disabled={busy !== null} onClick={() => onChoose("facebook")}>
            {busy === "facebook" ? "Opening Facebook…" : undefined}
          </SocialButton>
        )}
      </div>
    </>
  );
}
